import { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import swal from 'sweetalert';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormSkeleton, InlineLoader } from '../loader';
import { aiService } from '../../service/api.service';

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Agent name is required'),
  welcomeMsg: Yup.string().trim().required('Welcome message is required'),
  purpose: Yup.string().trim().required('Purpose is required'),
  tone: Yup.string().trim().required('Tone is required'),
  systemPrompt: Yup.string().trim().required('Guidance is required'),
  qualification_questions: Yup.string().required('Qualification questions are required'),
  escalation_rules: Yup.string().required('Escalation rules are required'),
});

const emptyValues = {
  name: '',
  welcomeMsg: '',
  purpose: '',
  tone: '',
  systemPrompt: '',
  emojis: false,
  leadGeneration: false,
  qualification_questions: '',
  escalation_rules: '',
};

export default function AgentCreate() {
  const [loader, setLoader] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');
  const [agentId, setAgentId] = useState(null);
  const [returnToAllAgents, setReturnToAllAgents] = useState(false);
  const [returnToAgentInfo, setReturnToAgentInfo] = useState(false);
  const [returnTo, setReturnTo] = useState(null);
  const [initialValues, setInitialValues] = useState(emptyValues);
  const navigate = useNavigate();
  const location = useLocation();

  const buildUserAgentListUrl = (id, name = "") => {
    const query = new URLSearchParams({ user_id: id });
    if (name) {
      query.append("user_name", name);
    }
    return `/agent-list?${query.toString()}`;
  };

  const navigateBackToAgentList = () => {
    if (returnToAgentInfo && agentId) {
      const query = new URLSearchParams({ agent_id: agentId });
      if (location.state?.agentName) query.append("agent_name", location.state.agentName);
      if (userId) query.append("user_id", userId);
      if (userName) query.append("user_name", userName);
      if (returnToAllAgents) query.append("from", "all");
      navigate(`/agent-info?${query.toString()}`, {
        state: {
          agentId,
          agentName: location.state?.agentName,
          userId,
          userName,
          userEmail: location.state?.userEmail,
          returnToAllAgents,
        },
      });
      return;
    }

    if (returnToAllAgents) {
      navigate('/allagentlist');
      return;
    }

    navigate(buildUserAgentListUrl(userId, userName), { state: { userId, userName } });
  };

  const navigateBackToOrigin = () => {
    if (!agentId && returnTo) {
      navigate(returnTo);
      return;
    }
    navigateBackToAgentList();
  };

  useEffect(() => {
    // Get userId/agentId from location state passed from List.jsx / AgentList.jsx
    if (location.state?.userId) {
      setUserId(location.state.userId);
    }
    if (location.state?.userName) {
      setUserName(location.state.userName);
    }
    if (location.state?.agentId) {
      setAgentId(location.state.agentId);
    }
    setReturnToAllAgents(!!location.state?.returnToAllAgents);
    setReturnToAgentInfo(!!location.state?.returnToAgentInfo);
    setReturnTo(location.state?.returnTo || null);
  }, [location]);

  useEffect(() => {
    if (agentId) {
      setFetching(true);
      aiService.getAgent(agentId)
        .then((res) => {
          const agent = res?.data?.data;
          if (agent) {
            setInitialValues({
              name: agent.name || '',
              welcomeMsg: agent.welcomeMsg || '',
              purpose: agent.purpose || '',
              tone: agent.tone || '',
              systemPrompt: agent.systemPrompt || '',
              emojis: !!agent.emojis,
              leadGeneration: !!agent.leadGeneration,
              qualification_questions: (agent.qualification_questions || []).join('\n'),
              escalation_rules: (agent.escalation_rules || []).join('\n'),
            });
          }
        })
        .catch(() => {
          swal({ icon: 'error', text: 'Failed to load agent details' });
        })
        .finally(() => setFetching(false));
    }
  }, [agentId]);

  const handleSubmit = async (values) => {
    if (!agentId && !userId) {
      swal({ icon: 'error', text: 'User ID is missing. Please go back and select a user.' });
      return;
    }

    setLoader(true);
    try {
      // Convert string arrays to actual arrays
      const qualificationQs = values.qualification_questions
        .split('\n')
        .map(q => q.trim())
        .filter(q => q);
      const escalationRules = values.escalation_rules
        .split('\n')
        .map(r => r.trim())
        .filter(r => r);

      const agentData = {
        name: values.name,
        welcomeMsg: values.welcomeMsg,
        purpose: values.purpose,
        tone: values.tone,
        systemPrompt: values.systemPrompt,
        emojis: values.emojis,
        leadGeneration: values.leadGeneration,
        qualification_questions: qualificationQs,
        escalation_rules: escalationRules,
      };

      if (agentId) {
        agentData.agent_id = agentId;
      } else {
        agentData.user_id = userId;
      }

      await aiService.createAgent(agentData);
      setLoader(false);
      swal({ icon: 'success', text: agentId ? 'Agent updated successfully!' : 'Agent created successfully!' });
      if (agentId) {
        navigateBackToAgentList();
      } else {
        navigateBackToOrigin();
      }
    } catch (error) {
      setLoader(false);
      swal({
        icon: 'error',
        text: error.response?.data?.message || error.message || `Failed to ${agentId ? 'update' : 'create'} agent`,
      });
    }
  };

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div>
              <h1 className="company-page-title">{agentId ? 'Edit Agent' : 'Create Agent'}</h1>
            </div>
          </div>
          <Row className="pb-4 justify-content-center">
            <Col md={12} lg={10} className="mx-auto">
              <div className="availabilit-section input-w-100 agent-create-shell">
                {fetching ? (
                  <FormSkeleton rows={7} />
                ) : (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mt-3">
                            <Form.Label>Agent Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              placeholder="e.g., Sales Assistant"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.name && !!errors.name}
                            />
                            <ErrorMessage name="name" component="div" className="text-danger" />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="mt-3">
                            <Form.Label>Tone</Form.Label>
                            <Form.Select
                              name="tone"
                              className="filter-select"
                              value={values.tone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.tone && !!errors.tone}
                            >
                              <option value="">Select tone...</option>
                              <option value="Friendly">Friendly</option>
                              <option value="Professional">Professional</option>
                              <option value="Formal">Formal</option>
                              <option value="Casual">Casual</option>
                            </Form.Select>
                            <ErrorMessage name="tone" component="div" className="text-danger" />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mt-3">
                        <Form.Label>Welcome Message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="welcomeMsg"
                          placeholder="e.g., Hello! How can I help you today?"
                          value={values.welcomeMsg}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.welcomeMsg && !!errors.welcomeMsg}
                        />
                        <ErrorMessage name="welcomeMsg" component="div" className="text-danger" />
                      </Form.Group>

                      <Form.Group className="mt-3">
                        <Form.Label>Purpose</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="purpose"
                          placeholder="e.g., Qualify sales leads and answer product questions"
                          value={values.purpose}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.purpose && !!errors.purpose}
                        />
                        <ErrorMessage name="purpose" component="div" className="text-danger" />
                      </Form.Group>

                      <Form.Group className="mt-3">
                        <Form.Label>Guidance</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="systemPrompt"
                          placeholder="e.g., Always greet politely. Ask qualifying questions before suggesting products."
                          value={values.systemPrompt}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.systemPrompt && !!errors.systemPrompt}
                        />
                        <ErrorMessage name="systemPrompt" component="div" className="text-danger" />
                      </Form.Group>

                      <Form.Group className="mt-3">
                        <Form.Label>Qualification Questions</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="qualification_questions"
                          placeholder="Enter one question per line:&#10;What product are you looking for?&#10;What is your budget?"
                          value={values.qualification_questions}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.qualification_questions && !!errors.qualification_questions}
                        />
                        <small className="text-muted">Enter one question per line</small>
                        <ErrorMessage name="qualification_questions" component="div" className="text-danger" />
                      </Form.Group>

                      <Form.Group className="mt-3">
                        <Form.Label>Escalation Rules</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="escalation_rules"
                          placeholder="Enter one rule per line:&#10;Customer requests refund&#10;Customer asks for a human"
                          value={values.escalation_rules}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.escalation_rules && !!errors.escalation_rules}
                        />
                        <small className="text-muted">Enter one rule per line</small>
                        <ErrorMessage name="escalation_rules" component="div" className="text-danger" />
                      </Form.Group>

                      <Row className="mt-3">
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="emojis"
                            name="emojis"
                            label="Use emojis in responses"
                            checked={values.emojis}
                            onChange={handleChange}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="leadGeneration"
                            name="leadGeneration"
                            label="Enable lead generation"
                            checked={values.leadGeneration}
                            onChange={handleChange}
                          />
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col md={12} className="d-flex gap-2">
                          <Button variant="primary" className="agent-primary-btn" type="submit" disabled={isSubmitting}>
                            {loader ? <InlineLoader /> : agentId ? 'Update Agent' : 'Create Agent'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              navigateBackToOrigin();
                            }}
                          >
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
}
